import { observer } from "mobx-react";
import { ReactNode, useEffect, useState } from "react";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import { Column, Row, Spacer } from "../../../components/Layout/Layout";
import { useSubNav } from "../../../components/LoggedinPage/SubNavContext";
import {
  storeTableState,
  useStoredTableState,
} from "../../../core/hooks/filters/useStoredTableState";
import useTranslations from "../../../core/i18n/useTranslations";
import Divider from "../../../ui-lib/components/Divider/Divider";
import PageHeader from "../../../ui-lib/components/PageHeader/PageHeader";
import { PAGE_ID as parentId } from "../Administrators";
import RoleTemplateTable from "./RoleTemplateTable";
import {
  getRoleTemplates,
  useRoleTemplates,
} from "../../../core/api/roleTemplates/roleTemplates";
import { useQueryState } from "../../../core/hooks/filters/useQueryState";
import { RoleTemplate } from "../../../core/api/roleTemplates/types";
import SearchInput from "../../../ui-lib/components/Inputs/SearchInput";
import { AddRoleTemplate } from "./AddRoleTemplate";
import useUser from "../../../core/user/useUser";
import { EditRoleTemplate } from "./EditRoleTemplate";
import { DeleteRoleTemplateModal } from "./DeleteRoleTemplateModal";
import {
  searchStringParser,
  urlSearchParser,
} from "../../../ui-lib/utils/urlSearchParser";
import {
  useDateTimeFormats,
  useTimezones,
} from "../../../core/api/administrators/administrators";
import LoadingSpinner from "../../../ui-lib/components/Loading/LoadingSpinner";
import { useModalIdHook } from "../../../core/hooks/modalIdsHandlers/useIdModalHook";
import { useFiltersByAdminType } from "../../../core/hooks/filters/useFiltersByAdminType";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import { ClearFilter } from "../../../ui-lib/components/Tables/CleatFilters";
import { getResponseCenters } from "../../../core/api/responsecenters/responsecenters";
import { notifyApiErrors } from "../../../core/helpers/helpers";

import styles from "../../PagesLayout.module.css";
import { CustomDropdown } from "../../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";

const parentPath = "/adminportal/administrators";
export const PAGE_ID = "adminportal/roletemplates";

const INITIAL_PAGE_SETTINGS = {
  page: 1,
  pageSize: 10,
  total: 0,
};

const subMenuItems = (url: string) => [
  { key: "Common:role_templates", to: url },
];

const RoleTemplatesWrapper = ({
  onCreateTemplate,
  onEditTemplate,
  resetId,
  children,
  selected,
  isEditTemplateOpen,
  setIsEditTemplateOpen,
}: {
  onCreateTemplate: () => void;
  onEditTemplate: () => void;
  resetId?: () => void;
  children: ReactNode;
  selected?: RoleTemplate;
  isEditTemplateOpen: boolean;
  setIsEditTemplateOpen: any;
}) => {
  const t = useTranslations();
  const user = useUser();

  const { data: timezones, isLoading: isTimezonesLoading } = useTimezones();
  const { data: dateTimeFormats, isLoading: isDateFormatsLoading } =
    useDateTimeFormats();

  if (isTimezonesLoading || isDateFormatsLoading) {
    return <LoadingSpinner theme="primary" />;
  }

  return (
    <>
      <>
        <PageHeader
          pageTitle
          title={t("Common:role_templates")}
          icon="identification"
        >
          {!!user.config?.show.find((key) => key === "UpdateRoleTemplates") && (
            <AddRoleTemplate
              onSubmit={onCreateTemplate}
              timezones={timezones}
              dateTimeFormats={dateTimeFormats}
            />
          )}
        </PageHeader>
        <Column
          className={styles.wrapper}
          style={{
            height: "auto",
            overflowY: "auto",
          }}
        >
          <Column
            className={styles.content}
            type="top"
            align="start"
            style={{ padding: "32px" }}
          >
            {children}
          </Column>
        </Column>
      </>
      {selected && (
        <EditRoleTemplate
          roleTemplateId={selected.id}
          onClose={() => {}}
          onSubmit={onEditTemplate}
          isEditTemplateOpen={isEditTemplateOpen}
          setIsEditTemplateOpen={setIsEditTemplateOpen}
          timezones={timezones}
          dateTimeFormats={dateTimeFormats}
          resetId={resetId}
        />
      )}
    </>
  );
};

const RoleTemplates = () => {
  const { path } = useRouteMatch();

  const t = useTranslations();
  const { authenticatedRequest } = useUser();

  storeTableState(PAGE_ID);
  const savedState = useStoredTableState(PAGE_ID);
  const searchState = urlSearchParser(savedState);
  const [pageSettings, setPageSettings] = useState(INITIAL_PAGE_SETTINGS);
  const [tableSettings, setTableSettings] = useQueryState<{ q: string }>({
    q: searchStringParser(searchState?.q) ?? "",
  });

  const [searchParam, setSearchParam] = useState<string>(
    searchStringParser(searchState?.q) ?? "",
  );

  const { FiltersByAdminType, setFiltersByAdminType, resetFiltersByAdminType } =
    useFiltersByAdminType(searchState);

  const [selectedOrg, setSelectedOrg] = useState<number | undefined>(
    FiltersByAdminType?.orgId ? +FiltersByAdminType.orgId : undefined,
  );

  const [id, setId, resetId] = useModalIdHook(
    searchState?.roleId || undefined,
    "roleId",
  );

  const adminTypeOptions: IItemProp[] = [
    {
      id: "Admin",
      name: t("Common:admin"),
      isSelected: FiltersByAdminType.adminType === "Admin",
    },
    {
      id: "Respondent",
      name: t("Common:respondent"),
      isSelected: FiltersByAdminType.adminType === "Respondent",
    },
  ];

  const [tableData, setTableData] = useState<RoleTemplate[]>([]);
  const [selected, setSelected] = useState<RoleTemplate>();
  const [arcList, setArcList] = useState<IItemProp[]>([]);

  const [isEditTemplateOpen, setIsEditTemplateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [arcLoading, setArcLoading] = useState(false);
  const [deleteTemplateModalOpen, setDeleteTemplateModalOpen] =
    useState<boolean>(false);

  const fetchAlarmReceptions = async () => {
    const query = {
      page: 1,
      pageSize: 1000,
      organizationId: selectedOrg,
    };
    try {
      setArcLoading(true);
      const data = await getResponseCenters(authenticatedRequest, query);
      const alarmTreeItems = data.data.map((item) => ({
        id: item.id!,
        name: item.name!,
        isSelected: item.id === Number(FiltersByAdminType.responsecenter),
      }));
      setArcList(alarmTreeItems);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setArcLoading(false);
    }
  };

  const { data, isLoading } = useRoleTemplates({
    page: pageSettings.page,
    pageSize: pageSettings.pageSize,
    q: tableSettings.q,
    organization:
      FiltersByAdminType.adminType === "Admin"
        ? FiltersByAdminType?.orgId
        : undefined,
    adminType: FiltersByAdminType?.adminType,
    responsecenter: FiltersByAdminType?.responsecenter,
  });

  const onClear = () => {
    setSearchParam("");
    setTableSettings((prev) => ({ ...prev, q: "" }));
    setPageSettings(INITIAL_PAGE_SETTINGS);
    setTableData([]);
    resetFiltersByAdminType();
    setArcList([]);
    setSelectedOrg(undefined);
  };

  const onTableUpdate = async () => {
    setLoading(true);
    try {
      const { data: templates } = await getRoleTemplates(
        {
          ...FiltersByAdminType,
          ...pageSettings,
          ...tableSettings,
          page: 1,
        },
        authenticatedRequest,
      );
      setTableData(templates.roleTemplates);
      setPageSettings({ ...INITIAL_PAGE_SETTINGS, total: templates.total });
    } catch (error: any) {
      notifyApiErrors(error.response?.data.errors);
    } finally {
      setLoading(false);
    }
  };

  const selectedAdminType = adminTypeOptions.find((type) => type.isSelected);

  useEffect(() => {
    if (
      FiltersByAdminType.adminType === "Respondent" &&
      !arcList.length &&
      selectedOrg
    ) {
      fetchAlarmReceptions();
    }
  }, [FiltersByAdminType.adminType, selectedOrg]);

  useEffect(() => {
    if (id.id) {
      const currentRole = tableData.find((role) => role.id === +id.id!);
      setIsEditTemplateOpen(true);
      setSelected(currentRole);
    } else {
      setSelected(undefined);
    }
  }, [tableData, id.id]);

  // Append new data to list once updated
  useEffect(() => {
    if (data) {
      setTableData(() => {
        setPageSettings({ ...pageSettings, total: data.total });
        return data.roleTemplates;
      });
    }
  }, [data]);

  const getSelectedTreeItem = () => {
    if (FiltersByAdminType.adminType === "Admin") {
      return FiltersByAdminType.orgId;
    }

    if (FiltersByAdminType.adminType === "Respondent") {
      return selectedOrg?.toString();
    }
    return undefined;
  };

  return (
    <Switch>
      <Route path={`${path}`}>
        <RoleTemplatesWrapper
          onCreateTemplate={onTableUpdate}
          onEditTemplate={onTableUpdate}
          selected={selected}
          isEditTemplateOpen={isEditTemplateOpen}
          setIsEditTemplateOpen={setIsEditTemplateOpen}
          resetId={resetId}
        >
          <Row className={styles.settingsRow}>
            <SearchInput
              placeholder={t("Administrator:roletemplate_search_placeholder")}
              value={searchParam ?? ""}
              limit={1}
              onChange={(value) => {
                if (value === searchParam && value === tableSettings.q) return;
                setSearchParam(value);
                setTableSettings((prev) => ({ ...prev, q: value }));
                setTableData([]);
                setPageSettings(INITIAL_PAGE_SETTINGS);
              }}
            />
          </Row>
          <Spacer size={16} />
          <Divider />
          <Spacer size={16} />
          <Row style={{ flex: "unset" }}>
            <Dropdown
              dataCy="role_template_type_filter"
              withClearItem
              variant="filter"
              placeholder={t("Administrator:type")}
              width={248}
              selectedItem={selectedAdminType}
              items={adminTypeOptions}
              onSelectItem={(item) => {
                if (item.id === FiltersByAdminType.adminType) return;
                // Clear all previosly selected filters
                resetFiltersByAdminType();
                setTableData([]);
                setSelectedOrg(undefined);
                setArcList([]);
                setPageSettings(INITIAL_PAGE_SETTINGS);
                // Set new filter value
                setFiltersByAdminType({
                  adminType: item.id === -1 ? undefined : item.id.toString(),
                });
              }}
            />
            <Spacer size={8} />
            {(FiltersByAdminType.adminType === "Admin" ||
              FiltersByAdminType.adminType === "Respondent") && (
              <CustomDropdown
                selectionMode="single"
                customerFilter={{ customers: getSelectedTreeItem() }}
                onSelect={(item) => {
                  if (FiltersByAdminType.adminType === "Respondent") {
                    setArcList([]);
                    setSelectedOrg(+item);
                    if (!item) {
                      setFiltersByAdminType({
                        responsecenter: undefined,
                      });
                    }
                  }
                  if (FiltersByAdminType.adminType === "Admin") {
                    setTableData([]);
                    setPageSettings(INITIAL_PAGE_SETTINGS);
                  }
                  setFiltersByAdminType({
                    orgId: item,
                  });
                }}
              />
            )}
            {FiltersByAdminType.adminType == "Respondent" && (
              <>
                <Spacer size={8} />
                <Dropdown
                  dataCy="role_template_arc_filter"
                  withClearItem
                  variant="filter"
                  withFilter
                  maxVisible={9}
                  bodyWidth={350}
                  width={250}
                  placeholder={t("Common:labels_alarm_reception")}
                  items={arcList || []}
                  disabled={arcLoading || !arcList?.length}
                  isLoading={arcLoading}
                  selectedItem={arcList?.find((arc) => arc?.isSelected)}
                  onSelectItem={(item) => {
                    if (
                      item.id.toString() === FiltersByAdminType.responsecenter
                    )
                      return;
                    setTableData([]);
                    setPageSettings(INITIAL_PAGE_SETTINGS);
                    setFiltersByAdminType({
                      ...FiltersByAdminType,
                      responsecenter:
                        +item.id === -1 ? undefined : item.id.toString(),
                    });
                    setArcList((prev) => {
                      return prev.map((arc) => {
                        arc.isSelected = arc.id === item.id;
                        return arc;
                      });
                    });
                  }}
                />
              </>
            )}
            <Spacer size={8} />
            <ClearFilter
              text={t("Common:labels_clear_all_filters")}
              onClearClick={onClear}
              filtersToWatch={[{ q: searchParam }, FiltersByAdminType]}
            />
          </Row>
          <Spacer size={16} />
          <Column className={styles.tableContainer} type="top">
            <RoleTemplateTable
              roleTemplates={tableData || []}
              isLoading={isLoading || arcLoading || loading}
              pageSettings={pageSettings}
              onPageSettingsChange={setPageSettings}
              onStartDelete={(role: RoleTemplate) => {
                setDeleteTemplateModalOpen(true);
                setSelected(role);
              }}
              onStartEdit={(role: RoleTemplate) => {
                setSelected(role);
                setId?.(role.id);
                setIsEditTemplateOpen(true);
              }}
            />
          </Column>
          <DeleteRoleTemplateModal
            isOpen={deleteTemplateModalOpen}
            templateId={selected?.id}
            onClose={() => {
              setDeleteTemplateModalOpen(false);
            }}
            onDelete={() => onTableUpdate()}
          />
        </RoleTemplatesWrapper>
      </Route>
    </Switch>
  );
};

const RoleTemplateContainer = () => {
  const t = useTranslations();

  const { url } = useRouteMatch();
  const { setSubNav } = useSubNav();
  const tableState = useStoredTableState(parentId);
  const backUrl = tableState ? `${parentPath}${tableState}` : parentPath;

  useEffect(() => {
    const nav = {
      menu: subMenuItems(url),
      backUrl,
      backText: t("Menu:administrators"),
    };

    setSubNav(nav);

    return () => {
      setSubNav(undefined);
    };
  }, []);

  return <RoleTemplates />;
};

export default observer(RoleTemplateContainer);
